import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Dialog, EmptyStateNoResults, Pagination, SearchInput, SimpleDataTable } from "@octopusdeploy/design-system-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { PackageResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { HowToCreateYourPackageDialogLink } from "~/areas/library/components/BuiltInRepository/HowToCreateYourPackageDialogLink";
import { HowToPushYourPackageDialogLink } from "~/areas/library/components/BuiltInRepository/HowToPushYourPackageDialogLink";
import { client } from "~/clientInstance";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { BuiltInRepositoryPageSidebar } from "./BuiltInRepositorySidebar";
import { PackageUpload } from "./UploadPackage";
export interface BuiltInRepositoryPageProps {
    spaceId: string;
    loaderData: BuiltInRepositoryPageLoaderData;
    queryParams: BuiltInRepositoryPageQueryParams;
    setQueryParams: QueryParamValuesSetter<BuiltInRepositoryPageQueryParams>;
}
export interface BuiltInRepositoryPageQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    filter: string;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
const pageTitle = "Built-in Package Repository";
export async function builtInRepositoryPageLoader(repository: Repository, queryParams: BuiltInRepositoryPageQueryParams): Promise<BuiltInRepositoryPageLoaderData> {
    return {
        packages: await getPaginatedPackages(repository, queryParams.page, queryParams.pageSize, queryParams.filter),
        totalPackages: (await repository.Feeds.getBuiltInStatus()).TotalPackages,
    };
}
const supportedPackageTypes = "NuGet, zip, tar, tar gzip, tar bzip2, jar, war, ear and rar";
export interface BuiltInRepositoryPageLoaderData {
    packages: ResourceCollection<PackageResource>;
    totalPackages: number;
}
export function BuiltInRepositoryPage({ spaceId, loaderData, queryParams, setQueryParams }: BuiltInRepositoryPageProps) {
    const [debouncedFilter, setDebouncedFilter] = useDebouncedExternalState(queryParams.filter, (value) => setQueryParams({ filter: value, page: 1 }), 200);
    const navigate = useSpaceAwareNavigation();
    const dispatchAction = useAnalyticActionDispatch();
    const uploadPackageDialogControls = useDialogTrigger();
    const { result: paginatedPackages } = usePaginatedPackagesQuery(loaderData, queryParams);
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const uploadPackagePageAction: PrimaryPageAction = {
        type: "button",
        hasPermissions: isAllowed({ permission: Permission.BuiltInFeedPush, project: "*" }),
        label: "Upload package",
        onClick: () => {
            dispatchAction("Upload Package", { resource: "Package", action: Action.Add });
            uploadPackageDialogControls.openDialog();
        },
    };
    const dialogs = (<>
            <Dialog open={uploadPackageDialogControls.isOpen}>
                <PackageUpload afterPackageUpload={(id) => navigate.navigate(links.packageDetailsPage.generateUrl({ spaceId, id }))} supportedPackageTypes={supportedPackageTypes}/>
            </Dialog>
        </>);
    if (loaderData.totalPackages === 0) {
        return (<>
                {dialogs}
                <PortalEmptyStateOnboarding title={"Upload or push packaged applications to the built-in package repository"} description={`The build-in repository accepts ${supportedPackageTypes} packages.`} primaryAction={uploadPackagePageAction} actions={[
                { type: "custom", key: "how-to-create-packages", content: <HowToCreateYourPackageDialogLink label={"How to create packages"} asSecondaryButton={true}/> },
                { type: "custom", key: "how-to-push-packages", content: <HowToPushYourPackageDialogLink label={"How to push packages"} asSecondaryButton={true}/> },
            ]}/>
            </>);
    }
    const hideBottomPagination = paginatedPackages.Items.length < 20;
    return (<PageContent header={{ title: pageTitle, primaryAction: uploadPackagePageAction }} filters={{ inputs: [<SearchInput value={debouncedFilter} onChange={setDebouncedFilter} accessibleName={"Search by package name"} placeholder={"Search by name"}/>] }} pagination={{
            ui: (<Pagination label="Packages" totalResults={paginatedPackages.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={paginatedPackages.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} sidebar={<BuiltInRepositoryPageSidebar />}>
            <SidebarBorderedContent>
                {dialogs}
                {paginatedPackages.TotalResults === 0 ? (<EmptyStateNoResults />) : (<SimpleDataTable data={paginatedPackages.Items} columns={[
                {
                    title: "ID",
                    render: (pkg) => (<InternalLink accessibleName={`View versions for ${pkg.PackageId}`} to={links.packageVersionsListPage.generateUrl({ spaceId, packageId: pkg.PackageId })}>
                                        {pkg.PackageId}
                                    </InternalLink>),
                },
                { title: "Highest version", render: (pkg) => <PackageVersionCell spaceId={spaceId} pkg={pkg}/> },
                { title: "Description", render: (pkg) => pkg.Description },
            ]} getRowKey={(pkg) => pkg.Id}/>)}
            </SidebarBorderedContent>
        </PageContent>);
}
interface PackageVersionCellProps {
    spaceId: string;
    pkg: PackageResource;
}
function PackageVersionCell({ spaceId, pkg }: PackageVersionCellProps) {
    return (<div className={packageVersionCellStyles}>
            <PermissionCheck permission={Permission.BuiltInFeedDownload} project="*">
                <a aria-label={`Download package ${pkg.Id} version ${pkg.Version}`} href={client.resolve(pkg.Links["Raw"])}>
                    <em className="fa-solid fa-download"/>
                </a>
                &nbsp;
            </PermissionCheck>
            <InternalLink accessibleName={`View package details for ${pkg.Id} version ${pkg.Version}`} to={links.packageDetailsPage.generateUrl({ spaceId, id: pkg.Id })}>
                {pkg.Version}
            </InternalLink>
        </div>);
}
const packageVersionCellStyles = css({
    display: "flex",
});
async function getPaginatedPackages(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, filter: string = "") {
    return repository.Packages.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        latest: true,
        filter,
    });
}
function usePaginatedPackagesQuery(initialResult: BuiltInRepositoryPageLoaderData, queryParams: BuiltInRepositoryPageQueryParams) {
    const { page, pageSize, filter } = queryParams;
    return useQuery((repository) => getPaginatedPackages(repository, page, pageSize, filter), [page, pageSize, filter], "Get paginated packages", { initialResult: initialResult.packages });
}
export function SidebarBorderedContent({ children }: {
    children: React.ReactNode;
}) {
    return <div className={sidebarBorderedContent}>{children}</div>;
}
const sidebarBorderedContent = css({
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderRadius: borderRadius.medium,
    overflow: "clip",
    borderRight: "none",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: "100%",
});
